import React, { useCallback, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { updateElementInCacheAction } from 'store/actions/element-cache-actions';
import Dropdown from 'components/common/dropdown-component';
import { SearchDropdownApi } from 'components/common/search-component';
import MenuApiData from 'models/utils/common/menu/menu-api-data-model';
import {
  SCALING_OPTIONS,
  UNIT_OPTIONS,
} from 'constants/feature/element-attribute-constants';
import Permissions from 'permissions/permissions';
import { _updateElementInStoreAndElementCacheByQuickAction } from 'store/actions/element-panel-actions';
import Draggable from 'react-draggable';
import ElementDetails from 'models/api/element-details-api-model';
import { FormattedMessage } from 'react-intl';
import { ReactComponent as Cancel } from 'icons/close-no-circle.svg';

const QUICK_ACTION_ELEMENT_ATTRIBUTE_BLOCK = 'quick_action_element-attributes';
const QUICK_ACTION_ELEMENT_ATTRIBUTE_ID_BLOCK =
  'quick_action_element-attribute-id';

const CANCEL_ICON_SIZE = '24px';

const PERIOD_DROPDOWN_DISPLAY_LIMIT = 5;
const ENTITY_DROPDOWN_DISPLAY_LIMIT = 5;
const MIN_CHAR_LENGTH_TO_SEARCH = 0;

const manageAttributeType = {
  entity: 'entity',
  scaling: 'scaling',
  units: 'units',
  period: 'period',
};

const ManageElementAttributesMenu = ({
  show,
  setShow,
  onCloseMenu,
  elementDetails,
  clickPosition,
}) => {
  const [search, setSearch] = React.useState('');
  const dispatch = useDispatch();
  const nodeRef = React.useRef(null);
  const storeData = useSelector((state) => state.data);
  const {
    selectedProject: { id },
    selectedStatement,
    elementPanel: { entityList, periodList },
  } = storeData;

  // Calculate initial drag position based on click or default to bottom-right
  const dragPosition = React.useMemo(() => {
    const menuWidth = 339;
    const menuHeight = 400; // Approximate height

    if (clickPosition) {
      // Ensure menu stays within viewport bounds
      let x = clickPosition.x - 50;
      let y = clickPosition.y - 20;

      // Adjust if menu would go off-screen
      if (x + menuWidth > window.innerWidth) {
        x = window.innerWidth - menuWidth - 20;
      }
      if (y + menuHeight > window.innerHeight) {
        y = window.innerHeight - menuHeight - 20;
      }
      if (x < 0) x = 20;
      if (y < 0) y = 20;

      return { x, y };
    }
    // Default to bottom-right corner with margin
    return {
      x: window.innerWidth - 359, // 339px width + 20px margin
      y: window.innerHeight - 520, // estimated height + margin
    };
  }, [clickPosition]);

  const onClose = useCallback(() => {
    setShow(false);
    if (onCloseMenu) {
      onCloseMenu();
    }
  }, [setShow, onCloseMenu]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!show) return;

    const handleClickOutside = (event) => {
      // Check if click is inside the menu
      if (nodeRef.current && nodeRef.current.contains(event.target)) {
        return;
      }

      // Check if click is on a dropdown menu or search results (they might be rendered outside via portal)
      const isDropdownClick = event.target.closest(
        '.dropdown-menu, .search-dropdown, .menu-wrapper, [role="listbox"], [role="option"]',
      );
      if (isDropdownClick) {
        return;
      }

      // Click is outside - close the menu
      setShow(false);
      if (onCloseMenu) {
        onCloseMenu();
      }
    };

    // Add small delay to avoid immediate close from the opening click
    const timerId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
    }, 150);

    return () => {
      clearTimeout(timerId);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [show, setShow, onCloseMenu]);

  const shouldDisable = useMemo(
    () =>
      !Permissions.Element.canEditElement(storeData, id) ||
      selectedStatement.isReadOnly(),
    [storeData, id, selectedStatement],
  );

  useEffect(() => {
    if (elementDetails.isLoaded) {
      setSearch(elementDetails.entity);
    }
  }, [elementDetails]);

  const handleAttributeUpdate = useCallback(
    ({ attribute, value }) => {
      const attributeSetters = {
        [manageAttributeType.entity]: () => elementDetails.setEntity(value),
        [manageAttributeType.scaling]: () =>
          elementDetails.setScaling({ scaling: value }),
        [manageAttributeType.units]: () =>
          elementDetails.setUnit({ units: value }),
        [manageAttributeType.period]: () =>
          elementDetails.setPeriod(value.value),
      };

      const setter = attributeSetters[attribute];
      if (!setter) return;

      const updatedElement = setter();
      dispatch(updateElementInCacheAction({ element: updatedElement }));
      dispatch(
        _updateElementInStoreAndElementCacheByQuickAction({
          elementDetails: updatedElement,
          isEntityUptate: attribute === manageAttributeType.entity,
        }),
      );
    },
    [elementDetails, dispatch],
  );

  const _setEntity = useCallback(
    ({ value }) => {
      setSearch(value);
      handleAttributeUpdate({
        attribute: manageAttributeType.entity,
        value,
      });
    },
    [handleAttributeUpdate],
  );

  const onSelectScaling = useCallback(
    (val) => {
      handleAttributeUpdate({
        attribute: manageAttributeType.scaling,
        value: val,
      });
    },
    [handleAttributeUpdate],
  );

  const onSelectUnits = useCallback(
    (option) => {
      handleAttributeUpdate({
        attribute: manageAttributeType.units,
        value: option,
      });
    },
    [handleAttributeUpdate],
  );

  const onSelectPeriod = useCallback(
    (period) => {
      handleAttributeUpdate({
        attribute: manageAttributeType.period,
        value: period,
      });
    },
    [handleAttributeUpdate],
  );

  const onClearSearch = useCallback(() => {
    setSearch('');
  }, []);

  const onChangeSearch = useCallback((val) => {
    setSearch(val);
  }, []);

  const onCreateEntity = useCallback(() => {
    handleAttributeUpdate({
      attribute: manageAttributeType.entity,
      value: search,
    });
  }, [handleAttributeUpdate, search]);

  // Memoize dropdown options to prevent recalculation during drag
  const periodDropdownOptions = useMemo(
    () => periodList?.getPeriodDropdownValues(),
    [periodList],
  );

  const periodDropdownValue = useMemo(
    () =>
      periodList?.hasPeriodDropdownValue(elementDetails?.period)
        ? periodList?.getPeriodDropdownValue(elementDetails?.period)
        : null,
    [periodList, elementDetails?.period],
  );

  const periodPlaceholder = useMemo(
    () =>
      !periodList?.hasPeriodDropdownValue(elementDetails?.period)
        ? elementDetails?.period
        : null,
    [periodList, elementDetails?.period],
  );

  const searchResultsApiModel = useMemo(
    () =>
      new MenuApiData({
        apiModel: entityList,
        options: entityList?.getEntityDropdownValues(search) || [],
      }),
    [entityList, search],
  );

  // Early return if no elementDetails
  if (!elementDetails) {
    console.error('ManageElementAttributesMenu: elementDetails is required');
    return null;
  }

  const menuContent = (
    <React.Fragment>
      {show && (
        <Draggable
          axis="both"
          nodeRef={nodeRef}
          handle={`.${QUICK_ACTION_ELEMENT_ATTRIBUTE_BLOCK}__header`}
          defaultPosition={dragPosition}
          enableUserSelectHack={false}
          scale={1}
        >
          <div ref={nodeRef} className={QUICK_ACTION_ELEMENT_ATTRIBUTE_BLOCK}>
            <div className={`${QUICK_ACTION_ELEMENT_ATTRIBUTE_BLOCK}__header`}>
              <span>
                <FormattedMessage id="quick-actions.label.elements.manage.attributes" />
              </span>
              <Cancel
                id={`${QUICK_ACTION_ELEMENT_ATTRIBUTE_BLOCK}--close-icon`}
                role={'button'}
                className={'close-button'}
                width={CANCEL_ICON_SIZE}
                height={CANCEL_ICON_SIZE}
                onClick={onClose}
              />
            </div>
            <div className={`${QUICK_ACTION_ELEMENT_ATTRIBUTE_BLOCK}__body`}>
              <div className={`${QUICK_ACTION_ELEMENT_ATTRIBUTE_BLOCK}__row`}>
                <div className={`${QUICK_ACTION_ELEMENT_ATTRIBUTE_BLOCK}__col`}>
                  <Dropdown
                    id={`${QUICK_ACTION_ELEMENT_ATTRIBUTE_ID_BLOCK}__scaling`}
                    label="element-panel.attributes.scaling.label"
                    options={SCALING_OPTIONS}
                    value={elementDetails.scaling}
                    placeholder="element-panel.attributes.scaling.label"
                    onSelectOption={onSelectScaling}
                    disabled={shouldDisable}
                  />
                </div>
                <div className={`${QUICK_ACTION_ELEMENT_ATTRIBUTE_BLOCK}__col`}>
                  <Dropdown
                    id={`${QUICK_ACTION_ELEMENT_ATTRIBUTE_ID_BLOCK}__unit-dropdown`}
                    label={'element-panel.attributes.unit.label'}
                    options={UNIT_OPTIONS}
                    value={elementDetails.units}
                    placeholder="element-panel.attributes.unit.label"
                    onSelectOption={onSelectUnits}
                    disabled={shouldDisable}
                  />
                </div>
              </div>
              <div className={`${QUICK_ACTION_ELEMENT_ATTRIBUTE_BLOCK}__row`}>
                <div className={`${QUICK_ACTION_ELEMENT_ATTRIBUTE_BLOCK}__col`}>
                  <Dropdown
                    id={`${QUICK_ACTION_ELEMENT_ATTRIBUTE_ID_BLOCK}__period-dropdown`}
                    label={'element-panel.attributes.period.label'}
                    options={periodDropdownOptions}
                    value={periodDropdownValue}
                    placeholder={periodPlaceholder}
                    onSelectOption={onSelectPeriod}
                    numberOfVisibleRows={PERIOD_DROPDOWN_DISPLAY_LIMIT}
                    disabled={shouldDisable}
                  />
                </div>
              </div>
              <div className={`${QUICK_ACTION_ELEMENT_ATTRIBUTE_BLOCK}__row`}>
                <div className={`${QUICK_ACTION_ELEMENT_ATTRIBUTE_BLOCK}__col`}>
                  <SearchDropdownApi
                    id={`${QUICK_ACTION_ELEMENT_ATTRIBUTE_ID_BLOCK}-entity-search`}
                    label={'element-panel.attributes.entity.label'}
                    className={`${QUICK_ACTION_ELEMENT_ATTRIBUTE_BLOCK}--search`}
                    placeholder={'common.search'}
                    onChange={onChangeSearch}
                    value={search}
                    isValid={true}
                    resultsApiModel={searchResultsApiModel}
                    onClear={onClearSearch}
                    isCreate={true}
                    onCreateClick={onCreateEntity}
                    minSearchCharLength={MIN_CHAR_LENGTH_TO_SEARCH}
                    showSearchButton={true}
                    onSelectResult={_setEntity}
                    numberOfVisibleRows={ENTITY_DROPDOWN_DISPLAY_LIMIT}
                    disabled={shouldDisable}
                  />
                </div>
              </div>
            </div>
          </div>
        </Draggable>
      )}
    </React.Fragment>
  );

  // Render using portal to avoid being clipped by parent containers
  return ReactDOM.createPortal(menuContent, document.body);
};

ManageElementAttributesMenu.propTypes = {
  // Property to control the visibility of the menu
  show: PropTypes.bool.isRequired,
  // Function to control the visibility of the menu
  setShow: PropTypes.func.isRequired,
  // Function to handle closing the menu
  onCloseMenu: PropTypes.func,
  // Details of the element for which the workpaper is being created
  elementDetails: PropTypes.instanceOf(ElementDetails).isRequired,
  /** Click position to render menu at */
  clickPosition: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
};

export default ManageElementAttributesMenu;
